import { MANAGE_ACCESS_PERMISSION } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { sessionQuery } from '@/lib/queries'

/**
 * Access control — the layout route. Owns the page header and the tab strip; each tab is a real
 * child route (/app/access/users, /app/access/roles), not client state, so tabs deep-link, show
 * up in breadcrumbs, and survive refresh.
 *
 * Everything under this page manages WorkOS state, not our database. Access to the app is
 * invitation-only — organization membership is the grant, and the Users tab is where
 * memberships come from (invitations) and go away (removal).
 *
 * Two audiences. People managers (`org-users:manage` — today only `admin`) get Users and
 * Directory; the Roles & permissions tab is the role editor's alone (`manage-all:users`), because
 * editing a role rewrites it for every organization. The nav and the tab strip only hide things —
 * the API gates each route on its own slug and is the boundary.
 */
export const Route = createFileRoute('/app/access')({
  component: AccessLayout,
})

const TABS = [
  { to: '/app/access/users', label: 'Users' },
  // Separate from Users because it answers a different question: Users is where somebody is
  // invited and given a platform role, this is the read across every account at once.
  { to: '/app/access/directory', label: 'Directory' },
  { to: '/app/access/roles', label: 'Roles & permissions', requires: MANAGE_ACCESS_PERMISSION },
] as const satisfies readonly { to: string; label: string; requires?: string }[]

function AccessLayout() {
  const { data: session } = useSuspenseQuery(sessionQuery)
  const granted = new Set(session.user?.permissions ?? [])
  const tabs = TABS.filter((tab) => !('requires' in tab) || granted.has(tab.requires))
  return (
    <div className="mx-auto flex w-full max-w-4xl animate-fade-in flex-col gap-6">
      <header className="mb-0">
        <h1 className="text-2xl font-semibold tracking-tight">Access control</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Users, roles, and permissions — managed in WorkOS. Changes reach signed-in users on their
          next session refresh, not instantly.
        </p>
      </header>

      <nav aria-label="Access control sections" className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className="-mb-px rounded-t-md border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            activeProps={{
              className: 'border-primary text-foreground',
              'aria-current': 'page',
            }}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      <Outlet />
    </div>
  )
}
